import { useWalletInfo } from '@reown/appkit/react';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { Connector, useConnect, useSwitchAccount } from 'wagmi';

import { SvgIconUrl } from 'common/components';

import { usePopup } from 'common/hooks';
import svgIcons from 'common/svgIcons';

import { memoizedComponent } from 'utils/memoizeComponent';

import Web3 from '../..';
import { Web3ConnectSuccessPopup } from '../../popups/ConnectSuccess/ConnectSuccess.component';
import './index.css';
import { useWeb3 } from '../../hooks/useWeb3.hook';

export const Web3Wallet = memoizedComponent(({ connector }: { connector: Connector }) => {
  const { switchAccountAsync, connectors } = useSwitchAccount();
  const { connector: activeWalletConnector } = useWeb3();
  const { connectAsync } = useConnect();
  const { show } = usePopup();
  const info = useWalletInfo();

  const checkConnection = useCallback(
    (_connector: Connector) => !!connectors.find((x) => x.id === _connector.id),
    [connectors],
  );

  const isConnected = useMemo(() => checkConnection(connector), [checkConnection, connector]);

  const onSubmit = useCallback(async () => {
    if (!isConnected) await connectAsync({ connector });
    await switchAccountAsync({ connector });

    if (!isConnected) {
      show({
        title: undefined,
        component: <Web3ConnectSuccessPopup />,
        wrapperClassName: 'web3-connect-success-wrapper',
      });
    }
  }, [connectAsync, connector, isConnected, show, switchAccountAsync]);

  const isActive = useMemo(() => connector.id === activeWalletConnector?.id, [activeWalletConnector?.id, connector.id]);

  const className = classNames({
    'web3-wallet': true,
    'web3-wallet--active': isActive,
    'web3-wallet--connected': isConnected,
  });

  const renderWalletIcon = useCallback(() => {
    if (connector?.icon) return <img className='web3-wallet__logo' src={connector?.icon} alt='' />;

    if (info.walletInfo?.icon)
      return <img className='web3-wallet__logo' src={info.walletInfo?.icon} alt={info.walletInfo?.name} />;

    return <SvgIconUrl className='web3-wallet__logo' symbol={svgIcons.WalletConnect()} />;
  }, [connector?.icon, info.walletInfo?.icon, info.walletInfo?.name]);

  return (
    <div key={connector.id} className={className} onClick={onSubmit}>
      <div className='web3-wallet__wrapper'>
        {renderWalletIcon()}
        <div className='flex flex-column flex-align-center gap-8'>
          <h2 className='web3-wallet__title'>{connector.name ?? info.walletInfo?.name}</h2>
          <Web3.Address className='web3-wallet__address' icon={false} />
          <Web3.Balance className='web3-wallet__balance' />
        </div>
      </div>
    </div>
  );
});
